import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import styles from "./Events.module.scss";
import { TimelineEvent } from "../../model/data";

interface EventsProps {
  events: TimelineEvent[];
  currentSlideIndex?: number;
  onSlideChange: (index: number) => void;
  resetKey?: number;
  isAnimating?: boolean;
}

export const Events: React.FC<EventsProps> = ({
  events,
  currentSlideIndex = 0,
  onSlideChange,
  resetKey,
  isAnimating = false,
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (swiperRef.current && resetKey !== undefined) {
      swiperRef.current.slideTo(0, 0);
      swiperRef.current.update();
      setIsBeginning(swiperRef.current.isBeginning);
      setIsEnd(swiperRef.current.isEnd);
    }
  }, [resetKey, events.length]);

  const handleSlideChange = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
    onSlideChange(swiper.activeIndex);
  };

  const handleAfterInit = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const handlePrev = () => {
    if (!isAnimating && swiperRef.current) swiperRef.current.slidePrev();
  };

  const handleNext = () => {
    if (!isAnimating && swiperRef.current) swiperRef.current.slideNext();
  };

  return (
    <div className={`${styles.events} ${isAnimating ? styles.isAnimating : ""}`}>
      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        onAfterInit={handleAfterInit}
        onResize={handleSlideChange}
        slidesPerView={1.2}
        spaceBetween={20}
        breakpoints={{
          481: {
            slidesPerView: 2.2,
            spaceBetween: 40,
          },
          1025: {
            slidesPerView: 3,
            spaceBetween: 80,
          },
        }}
        className={styles.swiper}
      >
        {events.map((event, index) => (
          <SwiperSlide key={index} className={styles.swiperSlide}>
            <div className={styles.event}>
              <div className={styles.eventYear}>{event.year}</div>
              <div className={styles.eventText}>{event.text}</div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {events.length > 1 && (
        <>
          {!isBeginning && (
            <button
              className={`${styles.navBtn} ${styles.prevBtn} ${isAnimating ? styles.disabled : ""}`}
              type="button"
              aria-label="Previous event"
              onClick={handlePrev}
              disabled={isAnimating}
            />
          )}
          {!isEnd && (
            <button
              className={`${styles.navBtn} ${styles.nextBtn} ${isAnimating ? styles.disabled : ""}`}
              type="button"
              aria-label="Next event"
              onClick={handleNext}
              disabled={isAnimating}
            />
          )}
        </>
      )}
    </div>
  );
};

